/* eslint-disable camelcase */
import { useMemo, useCallback, useState } from 'react';
import uniqBy from 'lodash/uniqBy';
import { useQuery } from '@apollo/react-hooks';
import {
	STORY_POINTS_ALIAS_FIELD_ID,
	STORY_POINT_ESTIMATE_ALIAS_FIELD_ID,
} from '@atlassian/jira-business-constants';
import {
	useGroupRecovery,
	type GenericGroupVariables,
} from '@atlassian/jira-business-list-handlers';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { ASSIGNEE_TYPE, PRIORITY_TYPE, STATUS_TYPE } from '@atlassian/jira-platform-field-config';
import {
	fireOperationalAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type {
	JiraBusinessBackgroundListViewItemGroupsWithPagination,
	JiraBusinessBackgroundListViewItemGroupsWithPagination_backgroundGroups_nodes,
	JiraBusinessBackgroundListViewItemGroupsWithPagination_backgroundGroups,
} from './__generated_apollo__/JiraBusinessBackgroundListViewItemGroupsWithPagination';
import type {
	JWMListViewItemGroupsWithPagination,
	JWMListViewItemGroupsWithPagination_groups,
	JWMListViewItemGroupsWithPaginationVariables,
} from './__generated_apollo__/JWMListViewItemGroupsWithPagination';
import { LIST_VIEW_ITEM_GROUPS_QUERY, BACKGROUND_LIST_VIEW_ITEMS_GROUPS_QUERY } from './gql';
import type { ItemGroupsInput, RecoveryReturn } from './types';
import { transformResult } from './utils';

const DEFAULT_ITEMS_PER_PAGE = 50;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const emptyArray: Array<any> = [];

const fieldIds = [
	ASSIGNEE_TYPE,
	PRIORITY_TYPE,
	STATUS_TYPE,
	STORY_POINTS_ALIAS_FIELD_ID,
	STORY_POINT_ESTIMATE_ALIAS_FIELD_ID,
];

export const getGroupVariables = ({
	groupByFieldId,
	jqlContext,
}: ItemGroupsInput): GenericGroupVariables => {
	const jql = jqlContext || '';

	return {
		jql,
		first: DEFAULT_ITEMS_PER_PAGE,
		groupByFieldId: groupByFieldId != null ? groupByFieldId : '',
		fieldIds,
	};
};

// Reloads the whole list and replaces the main list after completed
export const useListViewGroupRecovery = (
	input: ItemGroupsInput,
	settings: {
		isVisible: boolean;
		updateInterval?: number;
		isDisabled?: boolean;
	},
): RecoveryReturn => {
	const variables = getGroupVariables(input);

	return useGroupRecovery<
		JWMListViewItemGroupsWithPaginationVariables,
		JWMListViewItemGroupsWithPagination_groups,
		JWMListViewItemGroupsWithPagination,
		JiraBusinessBackgroundListViewItemGroupsWithPagination_backgroundGroups_nodes,
		JiraBusinessBackgroundListViewItemGroupsWithPagination_backgroundGroups,
		JiraBusinessBackgroundListViewItemGroupsWithPagination
	>({
		settings,
		queryList: LIST_VIEW_ITEM_GROUPS_QUERY,
		queryBackgroundList: BACKGROUND_LIST_VIEW_ITEMS_GROUPS_QUERY,
		variables,
		onError: (apolloError) => {
			fireErrorAnalytics({
				meta: {
					id: 'listRecoveryGroupQuery',
					packageName: 'jiraBusinessList',
					teamName: 'keystone',
				},
				attributes: {
					message: 'Failed to fetch issues in background in list view while grouping',
				},
				error: apolloError,
				sendToPrivacyUnsafeSplunk: true,
			});
		},
	});
};

export const useListItemGroups = ({ input }: { input: ItemGroupsInput }) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isLoadingNextPage, setIsLoadingNextPage] = useState(false);

	const { data, loading, error, fetchMore } = useQuery<
		JWMListViewItemGroupsWithPagination,
		JWMListViewItemGroupsWithPaginationVariables
	>(LIST_VIEW_ITEM_GROUPS_QUERY, {
		variables: getGroupVariables(input),
		onCompleted: (responseData) => {
			fireOperationalAnalytics(createAnalyticsEvent({}), 'listViewItemGroupsQuery succeeded', {
				groupsCount: responseData.groups?.nodes?.length,
			});
		},
		onError: (apolloError) => {
			fireErrorAnalytics({
				meta: {
					id: 'listViewItemGroupsQuery',
					packageName: 'jiraBusinessList',
					teamName: 'keystone',
				},
				attributes: {
					message: 'Failed to fetch item groups in list view',
				},
				error: apolloError,
				sendToPrivacyUnsafeSplunk: true,
			});
		},
	});

	const totalCount = data?.groups?.totalCount || 0;
	const nodes = data?.groups?.nodes || emptyArray;

	const hasNextPage = data?.groups?.pageInfo?.hasNextPage || false;
	const endCursor = data?.groups?.pageInfo?.endCursor || '';

	const result = useMemo(() => {
		if (!Array.isArray(nodes) || nodes.length === 0) {
			return [];
		}

		return transformResult(nodes);
	}, [nodes]);

	const loadNextPage = useCallback(() => {
		/*
		 * Do not load the same page twice and do not load the next page if
		 * a request is already in progress.
		 */
		if (!hasNextPage || loading) {
			return;
		}

		setIsLoadingNextPage(true);
		fireUIAnalytics(createAnalyticsEvent({}), 'table endReached');
		fetchMore({
			variables: { ...getGroupVariables(input), after: endCursor },
			// @ts-expect-error - The expected type comes from property 'updateQuery' which is declared here on type 'FetchMoreQueryOptions<JWMListViewItemGroupsWithPaginationVariables, keyof
			updateQuery: (prev, { fetchMoreResult }) => {
				setIsLoadingNextPage(false);
				if (!fetchMoreResult) return prev;

				const updatedNodes = [
					...(prev?.groups?.nodes || []),
					...(fetchMoreResult?.groups?.nodes || []),
				];
				return {
					groups: {
						...fetchMoreResult.groups,
						nodes: uniqBy(updatedNodes, 'id'),
					},
				};
			},
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			onError: (apolloError: any) => {
				fireErrorAnalytics({
					meta: {
						id: 'listViewItemGroupsQuery',
						packageName: 'jiraBusinessList',
						teamName: 'keystone',
					},
					attributes: {
						message: 'Failed to fetch more item groups in list view',
					},
					error: apolloError,
					sendToPrivacyUnsafeSplunk: true,
				});
			},
		});
	}, [createAnalyticsEvent, endCursor, fetchMore, hasNextPage, input, loading]);

	return {
		data: result,
		loading,
		error,
		totalCount,
		hasNextPage,
		isLoadingNextPage,
		loadNextPage,
	};
};
